/**
 * UserProfileDrawer — full user profile (analytics, usage, earnings,
 * behaviour) accessible from any clickable user row in the Admin
 * Dashboard. Read-only by design: the dedicated action buttons on the
 * list row still own the write actions.
 *
 * Open with: <UserProfileDrawer userId={u.user_id} onClose={…} />
 *
 * Built Jun 4 2026 — answers the user's "make all writer + coadmin
 * profiles clickable, show analytics/usage/earnings/behaviour" request.
 * Jun 4 2026 v2: every stat tile + genre pill is now a deep-link into
 * the existing admin breakdown / shop / writer-journey / improve pages.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Loader2, Calendar, Activity, Coins, Trophy, Flame, Award, Headphones,
  BookOpen, Film, Pen, Users as UsersIcon, Sparkles, Shield, AlertTriangle,
  Globe, Star, Check, Lock, Send, MessageCircle, Crown,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { labelForCountry } from '../utils/countries';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};
const fmtRel = (iso) => {
  if (!iso) return '—';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} d ago`;
  return fmtDate(iso);
};
const fmtCount = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
};
const fmtDur = (sec) => {
  if (!sec) return '0 m';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

const ROLE_STYLES = {
  admin:    { c: 'bg-red-600/20 text-red-300 border-red-500/40', label: 'ADMIN' },
  coadmin:  { c: 'bg-cyan-600/20 text-cyan-300 border-cyan-500/40', label: 'CO-ADMIN' },
  writer:   { c: 'bg-purple-600/20 text-purple-300 border-purple-500/40', label: 'WRITER' },
  pending_writer: { c: 'bg-amber-600/20 text-amber-300 border-amber-500/40', label: 'RETAIN PENDING' },
  user:     { c: 'bg-blue-600/20 text-blue-300 border-blue-500/40', label: 'USER' },
};

const StatTile = ({ icon: Icon, label, value, hint, accent = 'indigo', onClick, testIdKey }) => {
  const isClickable = typeof onClick === 'function';
  const Tag = isClickable ? 'button' : 'div';
  return (
    <Tag
      type={isClickable ? 'button' : undefined}
      onClick={isClickable ? onClick : undefined}
      className={`rounded-xl bg-slate-900/60 border border-slate-800 p-3 text-left transition ${
        isClickable ? 'hover:border-slate-600 hover:bg-slate-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 cursor-pointer' : ''
      }`}
      data-testid={`profile-stat-${testIdKey || label}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
        {Icon && <Icon className={`w-3.5 h-3.5 text-${accent}-300`} />}
        <span>{label}</span>
        {isClickable && <span className="ml-auto text-slate-500 text-[10px]">→</span>}
      </div>
      <p className="text-xl font-extrabold text-white mt-0.5 tabular-nums">{value}</p>
      {hint && <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>}
    </Tag>
  );
};

const Section = ({ title, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <h3 className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{title}</h3>
    {children}
  </section>
);

const UserProfileDrawer = ({ userId, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  // ── Grant Premium (admin-only) state ───────────────────────────────
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantTier, setGrantTier] = useState('premium');
  const [grantDays, setGrantDays] = useState(30);
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);
  const [viewerRole, setViewerRole] = useState(null);

  // Fetch viewer's own role once so we only render the admin-only
  // "Grant premium" CTA when the viewer is actually an admin (not a
  // co-admin viewing an admin panel — co-admins are read-only).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/auth/me`);
        const d = await r.json().catch(() => ({}));
        if (!cancelled) setViewerRole(d?.role || null);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleGrantPremium = async () => {
    if (!grantDays || grantDays < 1) {
      toast.error('Days must be at least 1');
      return;
    }
    setGranting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/grant-premium`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_user_id: userId,
          tier: grantTier,
          days: Number(grantDays),
          note: grantNote.trim() || undefined,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Grant failed');
      toast.success(
        `${grantTier.replace('_', ' ')} granted for ${grantDays}d (until ${new Date(d.premium_until).toLocaleDateString()})`,
      );
      setGrantOpen(false);
      setGrantNote('');
    } catch (err) {
      toast.error(err.message || 'Grant failed');
    } finally {
      setGranting(false);
    }
  };

  const handleRevokePremium = async () => {
    if (!window.confirm('Revoke this user\'s premium tier immediately?')) return;
    setGranting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/revoke-premium`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_user_id: userId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Revoke failed');
      toast.success('Premium revoked');
    } catch (err) {
      toast.error(err.message || 'Revoke failed');
    } finally {
      setGranting(false);
    }
  };

  const handleSendMessage = async () => {
    const body = messageBody.trim();
    if (!body) {
      toast.error('Write something before sending');
      return;
    }
    setSending(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/dm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_id: userId, body }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Failed to send message');
      toast.success('Message sent');
      setMessageBody('');
      setComposerOpen(false);
    } catch (err) {
      toast.error(err.message || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  // Closes the drawer then routes — guarantees only ONE drawer remains
  // open and the deep-link page renders with a clean slate.
  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/users/${userId}/profile`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) throw new Error(d.detail || 'Failed to load profile');
        setData(d);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const u = data?.user || {};
  const b = data?.behaviour || {};
  const e = data?.earnings || {};
  const o = data?.output || {};
  const co = data?.coadmin;
  const roleMeta = ROLE_STYLES[(u.role || 'user').toLowerCase()] || ROLE_STYLES.user;
  // Show writer-side panels (Earnings + Output) whenever there's ANY
  // signal that the user has authored content — by role, by writer_stats,
  // or by any uploaded series/ebooks. This catches admins who publish
  // their own content (which broke the previous `role === 'writer'`
  // gate) and writers-turned-coadmin.
  const hasAnyOutput = (o?.audiobook_series || 0) + (o?.cinematic_series || 0)
    + (o?.ebooks || 0) + (o?.episodes || 0) + (o?.chapters || 0) > 0;
  const showWriterPanels = (
    (u.role || '').toLowerCase() === 'writer'
    || (u.role || '').toLowerCase() === 'admin'
    || e?.has_writer_stats
    || (e?.writer_xp || 0) > 0
    || (e?.coin_balance || 0) > 0
    || hasAnyOutput
  );

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-stretch justify-end p-0 sm:p-0"
      onClick={onClose}
      data-testid="user-profile-drawer"
    >
      <div
        className="w-full sm:max-w-md bg-slate-900 border-l border-slate-800 shadow-2xl overflow-y-auto h-[92vh] sm:h-full rounded-t-2xl sm:rounded-none"
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header */}
        <header className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 p-4 flex items-center gap-3">
          <h2 className="text-sm font-bold text-white flex-1">User profile</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400"
            data-testid="user-profile-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {loading && (
          <div className="p-6 text-center text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" /> Loading profile…
          </div>
        )}

        {error && (
          <div className="p-6 text-center text-rose-300 text-sm">
            <AlertTriangle className="w-4 h-4 inline-block mr-1" /> {error}
          </div>
        )}

        {!loading && !error && data && (
          <div className="p-4 space-y-5">
            {/* Identity */}
            <div className="flex items-center gap-3">
              {u.picture ? (
                <img src={u.picture} alt="" className="w-14 h-14 rounded-full border border-slate-700 shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-indigo-600 flex items-center justify-center shrink-0">
                  <UsersIcon className="w-7 h-7 text-white" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-bold text-white truncate" data-testid="profile-name">{u.name || 'Unnamed'}</h3>
                <p className="text-xs text-slate-400 truncate" data-testid="profile-email">{u.email}</p>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${roleMeta.c}`}>{roleMeta.label}</span>
                  {u.id_verified && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-blue-600/20 text-blue-300 border-blue-500/40 inline-flex items-center gap-1">
                      <Check className="w-3 h-3" /> ID Verified
                    </span>
                  )}
                  {u.vault_access_granted && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-fuchsia-600/20 text-fuchsia-300 border-fuchsia-500/40 inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Vault
                    </span>
                  )}
                  {u.spam_risk && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-rose-600/20 text-rose-300 border-rose-500/40 inline-flex items-center gap-1" data-testid="profile-spam-risk">
                      <AlertTriangle className="w-3 h-3" /> Spam-risk
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Send Message — admins / co-admins can DM any user from here. */}
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              data-testid="profile-send-message-btn"
            >
              <MessageCircle className="w-4 h-4" /> Send message
            </button>

            {composerOpen && (
              <div className="rounded-xl bg-slate-950/80 border border-indigo-500/40 p-3 space-y-2" data-testid="profile-message-composer">
                <p className="text-[11px] text-slate-400">
                  Sending a direct message to <strong className="text-white">{u.name || u.email}</strong>.
                </p>
                <textarea
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value.slice(0, 4000))}
                  rows={4}
                  placeholder="Write your message…"
                  className="w-full rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
                  data-testid="profile-message-textarea"
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500 tabular-nums">{messageBody.length}/4000</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setComposerOpen(false); setMessageBody(''); }}
                      className="px-3 py-1.5 rounded-md text-xs font-bold text-slate-300 hover:bg-slate-800"
                      data-testid="profile-message-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSendMessage}
                      disabled={sending || !messageBody.trim()}
                      className="px-3 py-1.5 rounded-md text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 flex items-center gap-1"
                      data-testid="profile-message-send"
                    >
                      {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Send
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Grant Premium — admin-only. Co-admins are explicitly read-only
                so this whole control hides for them; the backend enforces
                the same guard. Lets an admin gift a paid tier for N days
                (or revoke instantly), audited in admin_grants. */}
            {viewerRole === 'admin' && (
              <>
                <button
                  type="button"
                  onClick={() => setGrantOpen((o) => !o)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-bold text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 shadow-lg shadow-amber-900/30"
                  data-testid="profile-grant-premium-btn"
                >
                  <Crown className="w-4 h-4" /> {grantOpen ? 'Close grant panel' : 'Grant premium'}
                </button>

                {grantOpen && (
                  <div className="rounded-xl bg-slate-950/80 border border-amber-500/40 p-3 space-y-2" data-testid="profile-grant-premium-panel">
                    <p className="text-[11px] text-slate-400">
                      Gift a subscription tier to <strong className="text-white">{u.name || u.email}</strong>.
                      Extends existing expiry — never shortens.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Tier</span>
                        <select
                          value={grantTier}
                          onChange={(e) => setGrantTier(e.target.value)}
                          className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                          data-testid="profile-grant-tier-select"
                        >
                          <option value="premium">Premium</option>
                          <option value="premium_top">Premium Top</option>
                          <option value="writer_ai_tools">Scribe AI Tools</option>
                        </select>
                      </label>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Days</span>
                        <input
                          type="number"
                          min={1}
                          max={3650}
                          value={grantDays}
                          onChange={(e) => setGrantDays(Math.max(1, Math.min(3650, Number(e.target.value) || 1)))}
                          className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500/40 tabular-nums"
                          data-testid="profile-grant-days-input"
                        />
                      </label>
                    </div>
                    <input
                      type="text"
                      value={grantNote}
                      onChange={(e) => setGrantNote(e.target.value.slice(0, 240))}
                      placeholder="Optional note (visible to recipient)…"
                      className="w-full rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                      data-testid="profile-grant-note-input"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={handleRevokePremium}
                        disabled={granting}
                        className="px-3 py-1.5 rounded-md text-xs font-bold text-rose-300 hover:bg-rose-950/40 border border-rose-500/30 disabled:opacity-50"
                        data-testid="profile-grant-revoke-btn"
                      >
                        Revoke now
                      </button>
                      <button
                        type="button"
                        onClick={handleGrantPremium}
                        disabled={granting || !grantDays}
                        className="px-4 py-1.5 rounded-md text-xs font-bold bg-amber-500 hover:bg-amber-400 text-slate-950 disabled:opacity-50 flex items-center gap-1"
                        data-testid="profile-grant-confirm-btn"
                      >
                        {granting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Crown className="w-3.5 h-3.5" />}
                        Grant {grantDays}d
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Account behaviour */}
            <Section title="Account behaviour" testId="profile-section-behaviour">
              <div className="grid grid-cols-2 gap-2">
                <StatTile icon={Calendar} label="Joined" value={fmtDate(u.created_at)} hint={`${u.account_age_days || 0} days ago`} accent="indigo"
                  testIdKey="joined"
                  onClick={() => go('/admin/breakdown/users')} />
                <StatTile icon={Activity} label="Last seen" value={fmtRel(u.last_activity_at)} accent="emerald"
                  testIdKey="last-seen"
                  onClick={() => go('/admin/breakdown/users')} />
                <StatTile icon={Shield} label="Auth" value={(u.auth_provider || 'email').toUpperCase()} accent="cyan"
                  testIdKey="auth" />
                <StatTile icon={Globe} label="Country" value={u.country ? labelForCountry(u.country) : '—'} accent="cyan"
                  testIdKey="country" />
              </div>
            </Section>

            {/* Reading + gamification */}
            <Section title="Reading & gamification" testId="profile-section-reading">
              <div className="grid grid-cols-2 gap-2">
                <StatTile icon={BookOpen} label="Books opened" value={fmtCount(b.books_opened)} accent="indigo"
                  testIdKey="books-opened"
                  onClick={() => go(`/library?reader=${encodeURIComponent(u.user_id)}`)} />
                <StatTile icon={Star} label="Books completed" value={fmtCount(b.books_completed)} accent="amber"
                  testIdKey="books-completed"
                  onClick={() => go(`/library?reader=${encodeURIComponent(u.user_id)}&filter=completed`)} />
                <StatTile icon={Headphones} label="Listening" value={`${b.listening_minutes || 0} m`} accent="emerald"
                  testIdKey="listening"
                  onClick={() => go('/admin/breakdown/content')} />
                <StatTile icon={Flame} label="Streak" value={`${b.streak_current} d`} hint={`Best: ${b.streak_best} d`} accent="amber"
                  testIdKey="streak"
                  onClick={() => go('/progress?ctx=skills')} />
                <StatTile icon={Coins} label="Coins" value={fmtCount(b.coins)} accent="amber"
                  testIdKey="coins"
                  onClick={() => go('/shop')} />
                <StatTile icon={Trophy} label="Level" value={b.level} hint={`${b.achievements_count} achievements`} accent="indigo"
                  testIdKey="level"
                  onClick={() => go('/profile/achievements')} />
              </div>
              {b.top_genres?.length > 0 && (
                <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 mt-2" data-testid="profile-top-genres">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">Top genres</p>
                  <div className="flex flex-wrap gap-1.5">
                    {b.top_genres.map((g) => (
                      <button
                        key={g.genre}
                        type="button"
                        onClick={() => go(`/library?genre=${encodeURIComponent(g.genre)}`)}
                        className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-[11px] text-slate-200 hover:border-indigo-400 hover:text-white transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                        data-testid={`profile-genre-${g.genre}`}
                        title={`Browse "${g.genre}" in the library`}
                      >
                        {g.genre} <span className="tabular-nums text-slate-400">({g.count})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Scribe earnings */}
            {showWriterPanels && (
              <Section title="Earnings & XP" testId="profile-section-earnings">
                <div className="grid grid-cols-2 gap-2">
                  <StatTile icon={Coins} label="Coin balance" value={fmtCount(e.coin_balance)} accent="amber"
                    testIdKey="coin-balance"
                    onClick={() => go('/shop')} />
                  <StatTile icon={Award} label="Lifetime earned" value={fmtCount(e.lifetime_coins_earned)} accent="amber"
                    testIdKey="lifetime-earned"
                    onClick={() => go('/profile/earnings')} />
                  <StatTile icon={Sparkles} label="Scribe XP" value={fmtCount(e.writer_xp)} hint={`Level ${e.writer_level} · ${fmtCount(e.writer_xp_in_level)} in level`} accent="indigo"
                    testIdKey="writer-xp"
                    onClick={() => go('/writer-journey')} />
                  <StatTile icon={Trophy} label="Evolution" value={e.writer_evolution || '—'} accent="indigo"
                    testIdKey="evolution"
                    onClick={() => go('/writer-journey')} />
                  <StatTile icon={Headphones} label="Unpaid audio" value={fmtDur(e.unpaid_audio_seconds)} accent="emerald"
                    testIdKey="unpaid-audio"
                    onClick={() => go('/profile/earnings')} />
                  <StatTile icon={Trophy} label="Paid out" value={`£${(e.paid_out_total || 0).toFixed(2)}`} accent="emerald"
                    testIdKey="paid-out"
                    onClick={() => go('/admin')} />
                </div>
              </Section>
            )}

            {/* Writer output */}
            {showWriterPanels && (
              <Section title="Published output" testId="profile-section-output">
                <div className="grid grid-cols-2 gap-2">
                  <StatTile icon={Headphones} label="Audiobook series" value={fmtCount(o.audiobook_series)} hint={`${o.chapters} chapters`} accent="emerald"
                    testIdKey="audiobook-series"
                    onClick={() => go('/manage-content?type=audiobook')} />
                  <StatTile icon={Film} label="Cinematic series" value={fmtCount(o.cinematic_series)} hint={`${o.episodes} episodes`} accent="cyan"
                    testIdKey="cinematic-series"
                    onClick={() => go('/manage-content?type=cinematic')} />
                  <StatTile icon={BookOpen} label="Ebooks" value={fmtCount(o.ebooks)} accent="indigo"
                    testIdKey="ebooks"
                    onClick={() => go('/manage-content?type=ebook')} />
                  <StatTile icon={Pen} label="Words published" value={fmtCount(o.words_published)} accent="indigo"
                    testIdKey="words-published"
                    onClick={() => go('/manage-content')} />
                  <StatTile icon={UsersIcon} label="Followers" value={fmtCount(o.followers)} accent="amber"
                    testIdKey="followers"
                    onClick={() => go('/admin/breakdown/writers')} />
                </div>
              </Section>
            )}

            {/* Co-admin meta */}
            {co && (
              <Section title="Co-admin assignment" testId="profile-section-coadmin">
                <div className="rounded-xl bg-cyan-500/5 border border-cyan-500/30 p-3 space-y-1">
                  <p className="text-xs text-cyan-200">
                    <strong>Assigned:</strong> {fmtDate(co.assigned_at)} ({fmtRel(co.assigned_at)})
                  </p>
                  <p className="text-xs text-cyan-200/80">
                    <strong>Previous role:</strong> {co.pre_coadmin_role || '—'}
                  </p>
                  <p className="text-xs text-cyan-200/80">
                    <strong>Assigned by:</strong> {co.assigned_by || '—'}
                  </p>
                </div>
              </Section>
            )}

            <p className="text-[10px] text-slate-600 text-center pt-3">
              ID: <span className="font-mono">{u.user_id}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileDrawer;
